import { AlbumNode, AlbumPage } from "@/engine/album";
import { Actor, HistoryDiff, NodeHistory, PageHistory } from "@/types/history";
import { localKeys } from "./constants";
import { AuthDataType } from "@/types/authTypes";
import { localData } from "@/services/helper";

// ─── Constants ──────────────────────────────────────────────────────────────
const ASSET_FIELDS = ["assetId", "thumbnailAssetId"] as const;

const IGNORED_KEYS = new Set([
    "createdAt",
    "commentCount",
    "_id",
    "updatedAt",
    "version",
    "parentId",
    "history",
    "id",
]);

function deepClone<T>(val: T): T {
    return JSON.parse(JSON.stringify(val));
}

function deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null || b == null) return a === b;
    if (typeof a !== typeof b) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        return a.every((item, i) => deepEqual(item, b[i]));
    }

    if (typeof a === "object" && typeof b === "object") {
        const aObj = a as Record<string, unknown>;
        const bObj = b as Record<string, unknown>;
        const keys = new Set([...Object.keys(aObj), ...Object.keys(bObj)]);
        for (const key of keys) {
            if (!deepEqual(aObj[key], bObj[key])) return false;
        }
        return true;
    }

    return false;
}

// ─── Diff two flat objects, ignoring system keys ─────────────────────────────
function deepDiff<T extends Record<string, unknown>>(
    before: T,
    after: T,
): { beforeDiff: Partial<T>; afterDiff: Partial<T>; hasChanges: boolean } {
    const beforeDiff: Partial<T> = {};
    const afterDiff: Partial<T> = {};

    const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]) as Set<
        keyof T & string
    >;

    // Remove system/meta keys
    for (const key of IGNORED_KEYS) {
        allKeys.delete(key as keyof T & string);
    }

    for (const key of allKeys) {
        if (key === "nodes") {
            // Compare nodes by ID only at page level
            const beforeIds = ((before[key] as any[]) ?? []).map((n: any) => n._id ?? n.id);
            const afterIds = ((after[key] as any[]) ?? []).map((n: any) => n._id ?? n.id);
            if (!deepEqual(beforeIds, afterIds)) {
                beforeDiff[key] = beforeIds as any;
                afterDiff[key] = afterIds as any;
            }
            continue;
        }

        const bVal = before[key] ?? null; // normalize undefined → null
        const aVal = after[key] ?? null; // normalize undefined → null

        if (!deepEqual(bVal, aVal)) {
            beforeDiff[key] = bVal as any;
            afterDiff[key] = aVal as any;
        }
    }

    return {
        beforeDiff,
        afterDiff,
        hasChanges: Object.keys(afterDiff).length > 0,
    };
}

// ─── Normalize node content so missing asset fields don't cause false diffs ──
function normalizeNodeContent(node: Record<string, unknown>): Record<string, unknown> {
    const content = deepClone((node.content ?? {}) as Record<string, unknown>);
    for (const field of ASSET_FIELDS) {
        if (!(field in content)) content[field] = null;
    }
    return { ...node, content };
}

// ─── Strip ignored keys from a node/page before diffing ─────────────────────
function stripIgnoredKeys<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const result: Partial<T> = {};
    for (const key of Object.keys(obj) as (keyof T)[]) {
        if (!IGNORED_KEYS.has(key as string)) {
            result[key] = obj[key];
        }
    }
    return result;
}



export function applyPageHistory(
    finalPage: AlbumPage,
    initialPage: AlbumPage,
    operation: "delete" | null = null,
): AlbumPage {
    const now = new Date().toISOString();

    const authData =
        localData.get<AuthDataType>(localKeys.authData);

    const actor: Actor = {
        id: authData?.id ?? "unknown",
        name: `${authData?.firstName ?? ""} ${
            authData?.lastName ?? ""
        }`.trim(),
        source: "user",
    };

    const initial = deepClone(initialPage);
    const final = deepClone(finalPage);

    const getNodeId = (node: any): string | null => {
        const id = node?._id ?? node?.id;

        if (id == null) {
            return null;
        }

        return String(id);
    };


    const initialPageFlat = stripIgnoredKeys(
        initial as any,
    );

    const finalPageFlat = stripIgnoredKeys(
        final as any,
    );

    delete (initialPageFlat as any).nodes;
    delete (finalPageFlat as any).nodes;

    const {
        beforeDiff: pageBeforeDiff,
        afterDiff: pageAfterDiff,
        hasChanges: pageChanged,
    } = deepDiff(
        initialPageFlat as Record<string, unknown>,
        finalPageFlat as Record<string, unknown>,
    );



    const initialNodeIds = (initial.nodes ?? [])
        .map(getNodeId)
        .filter(
            (id): id is string =>
                id !== null,
        );

    const finalNodeIds = (final.nodes ?? [])
        .map(getNodeId)
        .filter(
            (id): id is string =>
                id !== null,
        );

    const nodeListChanged = !deepEqual(
        initialNodeIds,
        finalNodeIds,
    );

    if (nodeListChanged) {
        (pageBeforeDiff as any).nodes =
            initialNodeIds;

        (pageAfterDiff as any).nodes =
            finalNodeIds;
    }



    const existingPageHistory =
        finalPage.history ?? [];

    const lastPageEntry =
        existingPageHistory[
            existingPageHistory.length - 1
        ];

    const currentPageVersion =
        finalPage.version ?? 1;

    const lastHistoryVersion =
        lastPageEntry?.version ?? 1;

    const nextPageVersion =
        Math.max(
            currentPageVersion,
            lastHistoryVersion,
        ) + 1;



const updatedNodes: AlbumNode[] =
    (final.nodes ?? []).map((node: any) => {
        const nodeId = getNodeId(node);

        const initialNode =
            initial.nodes.find(
                (candidate: any) =>
                    getNodeId(candidate) === nodeId,
            );

        if (!initialNode) {
            return node;
        }
        if (operation === "delete") {
            return node;
        }

        const hadImage =
            !!initialNode.content?.assetId;

        const hasImage =
            !!node.content?.assetId;

        const isInitialImageAssignment =
            !hadImage && hasImage;

        if (isInitialImageAssignment) {
            return node;
        }

        const normalizedInitial =
            normalizeNodeContent(
                stripIgnoredKeys(
                    initialNode as any,
                ) as Record<string, unknown>,
            );

        const normalizedNode =
            normalizeNodeContent(
                stripIgnoredKeys(
                    node as any,
                ) as Record<string, unknown>,
            );

        const {
            beforeDiff: rawBeforeDiff,
            afterDiff: rawAfterDiff,
            hasChanges,
        } = deepDiff(
            normalizedInitial,
            normalizedNode,
        );

        if (!hasChanges) {
            return node;
        }

        const nodeBeforeDiff =  deepClone(rawBeforeDiff);

        const nodeAfterDiff = deepClone(rawAfterDiff);

        const initialAssetId =  initialNode.content?.assetId ?? null;

        const finalAssetId = node.content?.assetId ?? null;

        const initialThumbnail = initialNode.content?.thumbnailAssetId ?? null;

        const finalThumbnail = node.content?.thumbnailAssetId ?? null;

        const assetChanged = initialAssetId !== finalAssetId || initialThumbnail !== finalThumbnail;

        if (!assetChanged) {
            if ((nodeBeforeDiff as any)?.content) {
                delete (nodeBeforeDiff as any).content.assetId;
                delete (nodeBeforeDiff as any).content.thumbnailAssetId;
                delete (nodeBeforeDiff as any).content.placeholder;
            }

            if ((nodeAfterDiff as any)?.content) {
                delete (nodeAfterDiff as any).content.assetId;
                delete (nodeAfterDiff as any).content.thumbnailAssetId;
                delete (nodeAfterDiff as any).content.placeholder;
            }

            if (
                (nodeBeforeDiff as any)?.content &&
                Object.keys(
                    (nodeBeforeDiff as any).content,
                ).length === 0
            ) {
                delete (nodeBeforeDiff as any).content;
            }

            if (
                (nodeAfterDiff as any)?.content &&
                Object.keys(
                    (nodeAfterDiff as any).content,
                ).length === 0
            ) {
                delete (nodeAfterDiff as any).content;
            }
        }

        if (
            Object.keys(nodeBeforeDiff as any).length === 0 &&
            Object.keys(nodeAfterDiff as any).length === 0
        ) {
            return node;
        }

        const existingHistory =
            node.history ?? [];

        const lastNodeEntry =
            existingHistory[
                existingHistory.length - 1
            ];

        const nodeVersion =
            nextPageVersion;

        const isNewVersion =
            !lastNodeEntry ||
            lastNodeEntry.version !== nodeVersion;

        let updatedHistory: NodeHistory[];

        if (isNewVersion) {
            updatedHistory = [
                ...existingHistory,
                {
                    action: "node:updated",
                    actor,
                    version: nodeVersion,
                    timestamp: now,
                    meta: {
                        label: "node_update",
                    },
                    diff: {
                        before: nodeBeforeDiff,
                        after: nodeAfterDiff,
                    } as HistoryDiff<any>,
                } as NodeHistory,
            ];
        } else {
            updatedHistory = [
                ...existingHistory.slice(0, -1),
                {
                    ...lastNodeEntry,
                    timestamp: now,
                    diff: {
                        before: {
                            ...(lastNodeEntry.diff?.before ?? {}),
                            ...nodeBeforeDiff,
                        },
                        after: nodeAfterDiff,
                    } as HistoryDiff<any>,
                } as NodeHistory,
            ];
        }

        return {
            ...node,
            version: nodeVersion,
            history: updatedHistory,
        };
    });


    const activeNodeIds = new Set(
        updatedNodes
            .map(getNodeId)
            .filter(
                (id): id is string =>
                    id !== null,
            ),
    );

    const persistentNodes: AlbumNode[] = [
        ...updatedNodes,
    ];

    for (const initialNode of initial.nodes ?? []) {
        const nodeId =
            getNodeId(initialNode);

        if (!nodeId) {
            continue;
        }

        if (!activeNodeIds.has(nodeId)) {
            persistentNodes.push(
                initialNode,
            );
        }
    }



    const updatedPage: AlbumPage = {
        ...finalPage,
        nodes: persistentNodes,
    };

    const pageChanged2 =
        pageChanged ||
        nodeListChanged;

    if (pageChanged2) {
        if (
            !lastPageEntry ||
            lastPageEntry.version !==
                nextPageVersion
        ) {
            updatedPage.history = [
                ...existingPageHistory,
                {
                    action: "page:updated",
                    version: nextPageVersion,
                    actor,
                    timestamp: now,
                    meta: {
                        label: "page_updated",
                    },
                    diff: {
                        before:
                            pageBeforeDiff,
                        after:
                            pageAfterDiff,
                    } as HistoryDiff<any>,
                } as PageHistory,
            ];
        } else {
            updatedPage.history = [
                ...existingPageHistory.slice(
                    0,
                    -1,
                ),
                {
                    ...lastPageEntry,
                    timestamp: now,
                    diff: {
                        before: {
                            ...(lastPageEntry
                                .diff
                                ?.before ??
                                {}),
                            ...pageBeforeDiff,
                        },
                        after: {
                            ...(lastPageEntry
                                .diff
                                ?.after ??
                                {}),
                            ...pageAfterDiff,
                        },
                    } as HistoryDiff<any>,
                } as PageHistory,
            ];
        }
        updatedPage.version =
            nextPageVersion;
    }

    return updatedPage;
}